import { useState } from "react";
import { withNamespaces } from "react-i18next";
import { connect } from "react-redux";
import { toast } from "react-toastify";
import { useQuery, useMutation } from "@tanstack/react-query";
import { withRouter } from "react-router-dom";
import {
    Input,
    Label,
    Button,
    Spinner,
} from "reactstrap";
import axios from "axios";
import {
    API_URL_GET_ORG_EMAIL_NOTIFICATIONS,
    API_URL_UPDATE_ORG_EMAIL_NOTIFICATIONS,
    API_URL_RESTORE_ORG_EMAIL_NOTIFICATIONS,
} from "src/modules/data-protection/constants";

const OrganizationEmailNotifications = ({
    t,
    token,
    Organization,
}) => {
    const [groups, setGroups] = useState([]);
    const [settings, setSettings] = useState({});

    const organizationId = Organization?.id;

    const applyResponseData = (data) => {
        const nextGroups = data?.groups || [];
        const nextSettings = data?.settings || {};

        // Prefer flat settings map; fall back to each notification.enabled from groups
        if (!Object.keys(nextSettings).length && nextGroups.length) {
            const fromGroups = {};
            nextGroups.forEach((group) => {
                (group.notifications || []).forEach((notification) => {
                    fromGroups[notification.type] = !!notification.enabled;
                });
            });
            setSettings(fromGroups);
        } else {
            setSettings(nextSettings);
        }

        setGroups(nextGroups);
    };

    const {
        isFetching: isFetchingSettings,
    } = useQuery({
        queryKey: ["gdpr-org-email-notifications", organizationId],
        queryFn: async () => {
            const response = await axios.get(API_URL_GET_ORG_EMAIL_NOTIFICATIONS, {
                params: { module: "gd" },
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data?.data || {};
        },
        enabled: !!organizationId,
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onSuccess: (data) => {
            applyResponseData(data);
        },
        onError: (error) => {
            const status = error?.response?.status;
            if (status === 403) {
                toast(t("Please upgrade your plan to access this feature"), {
                    type: "error",
                });
                return;
            }

            toast(t("An error occurred while fetching notifications settings."), {
                type: "error",
            });
        },
    });

    const {
        isLoading: isSaving,
        mutate: mutateSave,
    } = useMutation({
        mutationFn: async () => {
            const response = await axios.post(
                API_URL_UPDATE_ORG_EMAIL_NOTIFICATIONS,
                {
                    module: "gd",
                    notifications: settings,
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            return response.data?.data || {};
        },
        onSuccess: (data) => {
            applyResponseData(data);
            toast(t("Settings saved successfully."), {
                type: "success",
            });
        },
        onError: (error) => {
            const status = error?.response?.status;
            if (status === 403) {
                toast(t("Please upgrade your plan to access this feature"), {
                    type: "error",
                });
                return;
            }

            toast(t("An error occurred while saving settings."), {
                type: "error",
            });
        },
    });

    const {
        isLoading: isRestoring,
        mutate: mutateRestore,
    } = useMutation({
        mutationFn: async () => {
            const response = await axios.post(
                API_URL_RESTORE_ORG_EMAIL_NOTIFICATIONS,
                {
                    module: "gd",
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            return response.data?.data || {};
        },
        onSuccess: (data) => {
            applyResponseData(data);
            toast(t("Settings restored successfully."), {
                type: "success",
            });
        },
        onError: (error) => {
            const status = error?.response?.status;
            if (status === 403) {
                toast(t("Please upgrade your plan to access this feature"), {
                    type: "error",
                });
                return;
            }

            toast(t("An error occurred while restoring settings."), {
                type: "error",
            });
        },
    });

    const handleToggle = (type, enabled) => {
        setSettings((prev) => ({
            ...prev,
            [type]: enabled,
        }));
    };

    const isBusy = isFetchingSettings || isSaving || isRestoring;

    return (
        <div className="settings">
            <div className="mb-3">
                <h4 className="card-title mb-1">
                    {t("Organization email notifications")}
                </h4>
                <p className="text-muted mb-0">
                    {t("Control which emails are sent for your organization.")}
                </p>
            </div>

            {isFetchingSettings ? (
                <>
                    <div className="notification-loading-item mb-3">
                        <div className="dt-field dt-skeleton dt-select-list" />
                    </div>
                    <div className="notification-loading-item mb-3">
                        <div className="dt-field dt-skeleton dt-select-list" />
                    </div>
                    <div className="notification-loading-item mb-3">
                        <div className="dt-field dt-skeleton dt-select-list" />
                    </div>
                </>
            ) : (
                <div>
                    {groups.map((group) => (
                        <div key={group.key} className="mb-4">
                            <h5 className="mb-3">
                                {t(group.label)}
                            </h5>
                            {(group.notifications || []).map((notification) => (
                                <div
                                    className="form-check form-switch mb-3"
                                    dir="ltr"
                                    key={notification.type}
                                >
                                    <Input
                                        id={`org-email-notification-${notification.type}`}
                                        type="checkbox"
                                        className="form-check-input"
                                        checked={
                                            settings[notification.type] !== undefined
                                                ? !!settings[notification.type]
                                                : !!notification.enabled
                                        }
                                        disabled={isBusy}
                                        onChange={(e) =>
                                            handleToggle(
                                                notification.type,
                                                e.target.checked
                                            )
                                        }
                                    />
                                    <Label
                                        htmlFor={`org-email-notification-${notification.type}`}
                                        className="form-check-label"
                                    >
                                        {t(notification.label)}
                                    </Label>
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            )}

            <div className="d-flex gap-2 justify-content-end mt-4">
                <Button
                    color="primary"
                    type="button"
                    disabled={isBusy || !groups.length}
                    onClick={() => mutateSave()}
                >
                    {isSaving ? (
                        <>
                            <Spinner className="me-2" size="sm" />
                            {t("Saving")}
                        </>
                    ) : (
                        t("Save")
                    )}
                </Button>
                <Button
                    color="secondary"
                    type="button"
                    disabled={isBusy}
                    onClick={() => mutateRestore()}
                >
                    {isRestoring ? (
                        <>
                            <Spinner className="me-2" size="sm" />
                            {t("Restore Defaults")}
                        </>
                    ) : (
                        t("Restore Defaults")
                    )}
                </Button>
            </div>
        </div>
    );
};

const mapStatetoProps = (state) => {
    const { token } = state.Login;
    const { Organization } = state;

    return {
        token,
        Organization,
    };
};

export default withNamespaces()(
    withRouter(connect(mapStatetoProps, null)(OrganizationEmailNotifications))
);
